import type { BeatLineViteConfig } from '../api/types';

const STORAGE_KEY = 'beatline_native_email_session_v1';
const REMOTE_FALLBACK = 'https://beatlinegame.com';

type EmailAuthWindow = Window & {
  BeatLineEmailAuth?: { open: () => void };
  beatlineViteConfig?: any;
  beatlineSettings?: any;
  ajaxUrl?: string;
  ajaxNonce?: string;
  __beatlineEmailAuthCaptureInstalled?: boolean;
  __beatlineEmailAuthConfig?: BeatLineViteConfig;
};

function truthy(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'yes' || value === 'on';
}

function remoteSite(config?: BeatLineViteConfig): string {
  return String(config?.remoteSiteUrl || (window as any).beatlineViteConfig?.remoteSiteUrl || REMOTE_FALLBACK).replace(/\/$/, '');
}

function endpoint(config: BeatLineViteConfig | undefined, route: string): string {
  return remoteSite(config) + '/wp-json/beatline/v1/' + route;
}

function saveSession(session: string): void {
  try { if (session) localStorage.setItem(STORAGE_KEY, session); } catch (_) {}
}

function getSavedSession(): string {
  try { return String(localStorage.getItem(STORAGE_KEY) || ''); } catch (_) { return ''; }
}

function clearSavedSession(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
}

function mergeConfig(base: BeatLineViteConfig, incoming: any): BeatLineViteConfig {
  const inc = incoming || {};
  return {
    ...base,
    ...inc,
    beatlineSettings: {
      ...(base.beatlineSettings || {}),
      ...(inc.beatlineSettings || {}),
      app: { ...((base.beatlineSettings || {}).app || {}), ...((inc.beatlineSettings || {}).app || {}) },
      auth: { ...((base.beatlineSettings || {}).auth || {}), ...((inc.beatlineSettings || {}).auth || {}) },
      billing: { ...((base.beatlineSettings || {}).billing || {}), ...((inc.beatlineSettings || {}).billing || {}) },
      spotifyApp: { ...((base.beatlineSettings || {}).spotifyApp || {}), ...((inc.beatlineSettings || {}).spotifyApp || {}) },
      push: { ...((base.beatlineSettings || {}).push || {}), ...((inc.beatlineSettings || {}).push || {}) },
      onlineAccess: { ...((base.beatlineSettings || {}).onlineAccess || {}), ...((inc.beatlineSettings || {}).onlineAccess || {}) },
      onlineDefaults: { ...((base.beatlineSettings || {}).onlineDefaults || {}), ...((inc.beatlineSettings || {}).onlineDefaults || {}) },
    }
  };
}

function applyRuntimeConfig(config: BeatLineViteConfig): void {
  const w = window as EmailAuthWindow;
  w.beatlineViteConfig = config as any;
  w.beatlineSettings = config.beatlineSettings as any;
  w.ajaxUrl = String(config.beatlineSettings?.ajaxUrl || '');
  w.ajaxNonce = String(config.beatlineSettings?.nonce || '');
}

async function postForm(config: BeatLineViteConfig | undefined, route: string, data: Record<string, string>): Promise<any> {
  const body = new URLSearchParams();
  Object.entries(data).forEach(([key, value]) => body.set(key, value));

  const res = await fetch(endpoint(config, route), {
    method: 'POST',
    body,
    mode: 'cors',
    cache: 'no-store',
  });

  let json: any = null;
  try { json = await res.json(); } catch (_) {}

  if (!res.ok || !json || json.success !== true) {
    throw new Error(String(json?.message || json?.error || 'Login mislukt. Controleer je gegevens.'));
  }

  return json;
}

function ensureStyles(): void {
  if (document.getElementById('beatline-native-email-auth-style')) return;

  const style = document.createElement('style');
  style.id = 'beatline-native-email-auth-style';
  style.textContent = [
    '.beatline-email-auth-modal{position:fixed!important;inset:0!important;z-index:2147483647!important;display:none;align-items:center;justify-content:center;background:rgba(3,8,18,.82);padding:20px;box-sizing:border-box;}',
    '.beatline-email-auth-modal.is-open{display:flex!important;}',
    '.beatline-email-auth-panel{width:min(430px,100%);background:linear-gradient(180deg,#12203a,#071020);border:1px solid rgba(255,255,255,.14);border-radius:24px;padding:22px;color:#fff;box-shadow:0 24px 80px rgba(0,0,0,.45);font-family:Arial,sans-serif;}',
    '.beatline-email-auth-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:12px;}',
    '.beatline-email-auth-title{font-size:23px;line-height:1.1;font-weight:900;margin:0;}',
    '.beatline-email-auth-close{border:0;background:rgba(255,255,255,.10);color:#fff;width:38px;height:38px;border-radius:999px;font-size:22px;cursor:pointer;}',
    '.beatline-email-auth-text{margin:0 0 16px;color:rgba(255,255,255,.78);font-size:14px;line-height:1.4;}',
    '.beatline-email-auth-field{display:block;margin:0 0 12px;}',
    '.beatline-email-auth-field span{display:block;font-size:13px;font-weight:700;margin-bottom:6px;color:rgba(255,255,255,.85);}',
    '.beatline-email-auth-field input{width:100%;box-sizing:border-box;border:1px solid rgba(255,255,255,.18);background:rgba(255,255,255,.08);color:#fff;border-radius:14px;padding:13px 14px;font-size:16px;outline:none;}',
    '.beatline-email-auth-actions{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:14px;}',
    '.beatline-email-auth-actions button{border:0;border-radius:14px;padding:13px 10px;font-weight:900;cursor:pointer;}',
    '.beatline-email-auth-login{background:#ff8a00;color:#111;}',
    '.beatline-email-auth-register{background:#1e90ff;color:#fff;}',
    '.beatline-email-auth-status{min-height:20px;margin-top:12px;font-size:13px;line-height:1.35;color:rgba(255,255,255,.78);}',
    '.beatline-email-auth-status.is-error{color:#ff9b9b;}',
    '.beatline-email-auth-status.is-ok{color:#8ff0b0;}'
  ].join('\n');

  document.head.appendChild(style);
}

function ensureModal(config: BeatLineViteConfig): HTMLElement {
  ensureStyles();

  let modal = document.getElementById('beatline-native-email-auth-modal') as HTMLElement | null;

  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'beatline-native-email-auth-modal';
    modal.className = 'beatline-email-auth-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = [
      '<div class="beatline-email-auth-panel" role="dialog" aria-modal="true" aria-labelledby="beatline-email-auth-title">',
      '<div class="beatline-email-auth-head">',
      '<h2 id="beatline-email-auth-title" class="beatline-email-auth-title">Inloggen met e-mailadres</h2>',
      '<button type="button" class="beatline-email-auth-close" aria-label="Sluiten">&times;</button>',
      '</div>',
      '<p class="beatline-email-auth-text">Log in of maak gratis een account aan.</p>',
      '<form id="beatline-email-auth-form">',
      '<label class="beatline-email-auth-field"><span>E-mailadres</span><input name="email" type="email" autocomplete="email" required></label>',
      '<label class="beatline-email-auth-field"><span>Wachtwoord</span><input name="password" type="password" autocomplete="current-password" required minlength="6"></label>',
      '<div class="beatline-email-auth-actions">',
      '<button class="beatline-email-auth-login" type="submit" name="mode" value="login">Inloggen</button>',
      '<button class="beatline-email-auth-register" type="submit" name="mode" value="register">Account maken</button>',
      '</div>',
      '<div class="beatline-email-auth-status" aria-live="polite"></div>',
      '</form>',
      '</div>'
    ].join('');

    document.body.appendChild(modal);

    const close = () => {
      modal?.classList.remove('is-open');
      modal?.setAttribute('aria-hidden', 'true');
    };

    modal.querySelector('.beatline-email-auth-close')?.addEventListener('click', close);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });

    const form = modal.querySelector('#beatline-email-auth-form') as HTMLFormElement | null;
    const status = modal.querySelector('.beatline-email-auth-status') as HTMLElement | null;

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();

      const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;
      const mode = submitter?.value === 'register' ? 'register' : 'login';
      const email = String((form.elements.namedItem('email') as HTMLInputElement | null)?.value || '').trim();
      const password = String((form.elements.namedItem('password') as HTMLInputElement | null)?.value || '');

      if (!email || !password) return;

      if (status) {
        status.className = 'beatline-email-auth-status';
        status.textContent = mode === 'register' ? 'Account wordt aangemaakt...' : 'Je wordt ingelogd...';
      }

      form.querySelectorAll('button,input').forEach((el) => ((el as HTMLInputElement | HTMLButtonElement).disabled = true));

      try {
        const liveConfig = (window as EmailAuthWindow).__beatlineEmailAuthConfig || config;
        const route = mode === 'register' ? 'native-email-register' : 'native-email-login';
        const json = await postForm(liveConfig, route, { email, password });

        if (json.session) saveSession(String(json.session));

        const merged = json.config ? mergeConfig(liveConfig, json.config) : liveConfig;
        (window as EmailAuthWindow).__beatlineEmailAuthConfig = merged;
        applyRuntimeConfig(merged);

        if (status) {
          status.className = 'beatline-email-auth-status is-ok';
          status.textContent = 'Gelukt. BeatLine wordt opnieuw geladen...';
        }

        window.setTimeout(() => {
          const target = 'https://localhost/?native_app=1&native_reason=email_auth_success&ts=' + encodeURIComponent(String(Date.now()));
          try { window.location.replace(target); }
          catch (_) { window.location.href = target; }
        }, 450);
      } catch (error: any) {
        if (status) {
          status.className = 'beatline-email-auth-status is-error';
          status.textContent = String(error?.message || error || 'Login mislukt.');
        }
        form.querySelectorAll('button,input').forEach((el) => ((el as HTMLInputElement | HTMLButtonElement).disabled = false));
      }
    });
  }

  // Altijd als laatste child zetten, zodat hij boven de member-modal komt.
  try { document.body.appendChild(modal); } catch (_) {}

  return modal;
}

function openEmailAuthModal(): void {
  const w = window as EmailAuthWindow;
  const config = w.__beatlineEmailAuthConfig || w.beatlineViteConfig;
  if (!config) return;

  const modal = ensureModal(config);
  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');

  window.setTimeout(() => {
    try { (modal.querySelector('input[name="email"]') as HTMLInputElement | null)?.focus(); } catch (_) {}
  }, 50);
}

function isEmailAuthTrigger(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;

  if (el.closest('.beatline-auth-email-btn')) return true;
  if (el.closest('[data-native-email-auth]')) return true;
  if (el.closest('[data-beatline-email-auth]')) return true;

  const clickable = el.closest('a,button,[role="button"]') as HTMLElement | null;
  const text = String(clickable?.textContent || '').trim().toLowerCase();

  return (
    text.includes('registreer/login met e-mailadres') ||
    text.includes('registreer/login met emailadres') ||
    text.includes('login met e-mailadres') ||
    text.includes('login met emailadres') ||
    text.includes('inloggen met e-mailadres') ||
    text.includes('inloggen met emailadres')
  );
}

function isEmailLogoutTrigger(target: EventTarget | null): boolean {
  if (!getSavedSession()) return false;

  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;

  if (el.closest('.auth-logout')) return true;
  if (el.closest('[data-auth-logout]')) return true;
  if (el.closest('[data-logout]')) return true;

  const clickable = el.closest('a,button,[role="button"]') as HTMLElement | null;
  const href = String((clickable as HTMLAnchorElement | null)?.href || '').toLowerCase();
  const text = String(clickable?.textContent || '').trim().toLowerCase();

  return href.includes('logout') || text === 'uitloggen' || text.includes('uitloggen');
}

function doEmailLogout(): void {
  clearSavedSession();

  try {
    const w = window as EmailAuthWindow;
    if (w.beatlineViteConfig?.beatlineSettings?.auth) {
      w.beatlineViteConfig.beatlineSettings.auth.loggedIn = 0;
    }
    if (w.beatlineSettings?.auth) {
      w.beatlineSettings.auth.loggedIn = 0;
    }
  } catch (_) {}

  const target = 'https://localhost/?native_app=1&native_reason=email_logout&ts=' + encodeURIComponent(String(Date.now()));
  try { window.location.replace(target); }
  catch (_) { window.location.href = target; }
}

function installCaptureClickHandler(): void {
  const w = window as EmailAuthWindow;
  if (w.__beatlineEmailAuthCaptureInstalled) return;

  w.__beatlineEmailAuthCaptureInstalled = true;

  document.addEventListener('click', (event) => {
    if (isEmailAuthTrigger(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof (event as any).stopImmediatePropagation === 'function') {
        (event as any).stopImmediatePropagation();
      }
      openEmailAuthModal();
      return;
    }

    if (isEmailLogoutTrigger(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof (event as any).stopImmediatePropagation === 'function') {
        (event as any).stopImmediatePropagation();
      }
      doEmailLogout();
    }
  }, true);
}

export async function resumeNativeEmailSession(config: BeatLineViteConfig): Promise<BeatLineViteConfig> {
  const w = window as EmailAuthWindow;
  w.__beatlineEmailAuthConfig = config;

  installCaptureClickHandler();

  if (!config.nativeApp) return config;
  if (truthy(config.beatlineSettings?.auth?.loggedIn) || config.userLoggedIn) return config;

  const session = getSavedSession();
  if (!session) return config;

  try {
    const json = await postForm(config, 'native-email-session', { session });

    if (json.session) saveSession(String(json.session));

    if (json.config) {
      const merged = mergeConfig(config, json.config);
      w.__beatlineEmailAuthConfig = merged;
      applyRuntimeConfig(merged);
      return merged;
    }
  } catch (_) {
    clearSavedSession();
  }

  return config;
}

export function installNativeEmailAuth(config: BeatLineViteConfig): void {
  const w = window as EmailAuthWindow;
  w.__beatlineEmailAuthConfig = config;

  installCaptureClickHandler();

  w.BeatLineEmailAuth = {
    open() {
      openEmailAuthModal();
    }
  };
}
